export type TifdSheetKind = 'BALANCE_SHEET' | 'INCOME';

export type TifdLineType = 'USER_DEFINED' | 'SYSTEM_GENERATED';

export type TifdExpectedSign = 'DEBIT' | 'CREDIT' | 'ANY';

export type TifdValidationSeverity = 'ERROR' | 'WARNING' | 'INFO';

export interface TifdActivity {
  activityNo: number;
  establishmentName: string;
  operationDescription: string;
}

export interface TifdLineSource {
  accountId: string;
  accountCode: string;
  accountName: string;
  amount: number;
}

export interface TifdLine {
  code: string;
  label: string;
  hint: string | null;
  type: TifdLineType;
  expectedSign: TifdExpectedSign;
  cellName: string;
  activityNo: number;
  exactAmount: number;
  roundedAmount: number;
  roundingDifference: number;
  sources: TifdLineSource[];
}

export interface TifdSection {
  name: string;
  lines: TifdLine[];
}

export interface TifdSheet {
  sheet: TifdSheetKind;
  sections: TifdSection[];
}

export interface TifdValidation {
  rule: string;
  severity: TifdValidationSeverity;
  message: string;
  details: string[];
}

/** Body of POST /tifd/{applicationConnectionId}/reports/preview. */
export interface TifdReport {
  id: string | null;
  status: string | null;
  reportTypeId: string;
  yearOfAssessment: number;
  periodStart: string;
  periodEnd: string;
  sourceType: string;
  currency: string;
  hasErrors: boolean;
  generatedDate: number | null;
  lastUpdatedDate: number | null;
  catalogueName: string;
  roundingCode: string | null;
  trialBalanceFetchedDate: number | null;
  yearEndDay: string;
  yearEndMonth: string;
  activities: TifdActivity[];
  sheets: TifdSheet[];
  validations: TifdValidation[];
}

/** Entity and mapping the preview was requested for. */
export interface TifdReportContext {
  connectionId: string;
  mappingId: string;
  applicationConnectionId: string;
}

export interface TifdReportRequest {
  periodStart: string;
  periodEnd: string;
  roundingCode?: string;
}

/** Body of PUT /tifd/{applicationConnectionId}/reports/{reportId}. */
export interface UpdateTifdReportRequest {
  refreshTrialBalance?: boolean;
  roundingCode?: string;
}

const UNSAVED_TIFD_STATUSES = new Set([
  'NOT_SAVED',
  'NOT_STARTED',
  'DRAFT',
  'Not Saved',
  'Not Started',
  'Draft',
]);

/** Workbook fill needs a persisted report that is no longer a draft. */
export const isTifdReportSaved = (report: TifdReport | null | undefined): boolean =>
  !!report?.id &&
  !!report.status &&
  !UNSAVED_TIFD_STATUSES.has(report.status);
